import { FC, useState } from 'react';
import PenIcon from '@icons/PenIcon/PenIcon.tsx';
import { FormInput } from '@components/FormInput/ui/FormInput.tsx';
import { IconButton } from '@shared/components';
import CheckIcon from '@icons/CheckIcon/CheckIcon.tsx';
import CrossIcon from '@icons/CrossIcon/CrossIcon.tsx';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import useToast from '@hooks/useToast';
import styles from './index.module.scss';

interface IEditableTextInputProps {
  value: string;
  wrapperClassName?: string;
  documentGeneratorId: number;
  onSuccess?: () => void;
  onUpdate: (id: number, data: Partial<any>) => Promise<any>;
  size?: 'small' | 'large';
  inputClassName?: string;
  editOnHover?: boolean;
}

interface RenderEditFieldProps {
  value: string;
  closeEditAction: () => void;
  documentGeneratorId: number;
  onSuccess?: () => void;
  onUpdate: (id: number, data: Partial<any>) => Promise<any>;
  size?: 'small' | 'large';
  inputClassName?: string;
}

/**
 * @param props - The props for the component.
 * @param value - Text input name.
 * @param wrapperClassName - Custom class name for text input container.
 * @param onSuccess - Function which is called after successful demand update
 * @param onUpdate - Function which is called after successful demand update
 * @param size - Text input size 'small' | 'large'
 * @description - a component to change demand version
 */

const EditDocumentGeneratorName: FC<IEditableTextInputProps> = ({
  wrapperClassName = '',
  value,
  documentGeneratorId,
  onSuccess,
  onUpdate,
  size = 'large',
  inputClassName,
  editOnHover,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const showEditField: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (isEditing) {
      event.stopPropagation();
    }

    if (!editOnHover) {
      setIsEditing(true);
    }
  };

  const showEditFieldPen: React.MouseEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
    setIsEditing(true);
  };

  return (
    <div
      className={[styles.editableTextWrapper, wrapperClassName].join(' ')}
      onClick={showEditField}
    >
      {!isEditing ? (
        <div
          className={classNames(
            styles.editableTextBox,
            editOnHover && styles.showEditOnHover
          )}
        >
          <p>{value}</p>
          <div onClick={showEditFieldPen}>
            <PenIcon />
          </div>
        </div>
      ) : (
        <RenderEditField
          documentGeneratorId={documentGeneratorId}
          value={value}
          onSuccess={onSuccess}
          onUpdate={onUpdate}
          size={size}
          inputClassName={inputClassName}
          closeEditAction={() => {
            setIsEditing(false);
          }}
        />
      )}
    </div>
  );
};

/**
 * @param props - The props for the component.
 * @param value - Text input name.
 * @param closeEditAction - Function for closing toast.
 * @param documentGeneratorId - Active demand id
 * @param onSuccess - Function which is called after successful demand update
 * @param onUpdate - Function which is called after successful demand update
 * @param size - Text input size 'small' | 'large'
 * @description - a component for editable input
 */

const RenderEditField: FC<RenderEditFieldProps> = ({
  value,
  closeEditAction,
  documentGeneratorId,
  onSuccess,
  onUpdate,
  size,
  inputClassName,
}) => {
  const { register, getValues } = useForm<{ name: string }>({
    defaultValues: {
      name: value,
    },
  });
  const { showError, showSuccess } = useToast();

  const onConfirm = async () => {
    const updatedName = getValues('name').trim();
    if (!updatedName) {
      showError('Error during changing name', 'Name cannot be empty');
      return;
    }

    try {
      await onUpdate(documentGeneratorId, { name: updatedName });
      if (onSuccess) {
        onSuccess();
      }
      showSuccess('Success update', 'Name has been changed successfully');
      closeEditAction();
    } catch (error) {
      console.error(error);
      showError('Update failed', 'An error occurred while updating the name.');
    }
  };

  return (
    <>
      <FormInput {...register('name')} className={inputClassName} />
      <IconButton
        variant="active"
        className={[styles.btn, size && styles[`${size}Btn`]].join(' ')}
        onClick={(e) => {
          e.stopPropagation();
          onConfirm();
        }}
      >
        <CheckIcon />
      </IconButton>
      <IconButton
        className={[styles.btn, size && styles[`${size}Btn`]].join(' ')}
        onClick={(e) => {
          e.stopPropagation();
          closeEditAction();
        }}
      >
        <CrossIcon stroke="#98A2B3" />
      </IconButton>
    </>
  );
};
export { EditDocumentGeneratorName };
